import { createContext, useContext, useState, ReactNode } from "react";
import { defaultFormatTemplate } from "@/data/email";
import { useSettings } from "@/context/SettingsContext";
import { Category, WordPressSettings } from "@/types/settings.types";
import { useWebsites } from "./WebsitesContext";
import { DEFAULT_IMAGE_MODEL } from "@/config/imageModels";

type SettingsStateContextType = {
  styleInput: string;
  setStyleInput: (val: string) => void;
  subjects: string[];
  setSubjects: (val: string[]) => void;
  newSubject: string;
  setNewSubject: (val: string) => void;
  isSaving: boolean;
  setIsSaving: (val: boolean) => void;
  isAuthenticating: boolean;
  setIsAuthenticating: (val: boolean) => void;
  showAuthDialog: boolean;
  setShowAuthDialog: (val: boolean) => void;
  wpUrl: string;
  setWpUrl: (val: string) => void;
  wpUsername: string;
  setWpUsername: (val: string) => void;
  wpPassword: string;
  setWpPassword: (val: string) => void;
  generatingSubject: string | null;
  setGeneratingSubject: (val: string | null) => void;
  connectionError: string | null;
  setConnectionError: (val: string | null) => void;
  connectionDiagnostics: any;
  setConnectionDiagnostics: (val: any) => void;
  directWpSettings: WordPressSettings | null;
  setDirectWpSettings: (val: WordPressSettings | null) => void;
  categories: Category[];
  setCategories: (val: Category[]) => void;
  wpFormatOpen: boolean;
  setWpFormatOpen: (val: boolean) => void;
  htmlTemplate: string;
  setHtmlTemplate: (val: string) => void;
  sendingTestPost: boolean;
  setSendingTestPost: (val: boolean) => void;
  testPostId: number | null;
  setTestPostId: (val: number | null) => void;
  testPostUrl: string | null;
  setTestPostUrl: (val: string | null) => void;
  websiteLanguage: string;
  setWebsiteLanguage: (val: string) => void;
  customLanguageModalOpen: boolean;
  setCustomLanguageModalOpen: (val: boolean) => void;
  customLanguageInput: string;
  setCustomLanguageInput: (val: string) => void;
  wpPublishStatus: string;
  setWpPublishStatus: (val: string) => void;
  showCategoryDialog: boolean;
  setShowCategoryDialog: (val: boolean) => void;
  generatedCategories: { name: string; slug: string }[];
  setGeneratedCategories: (val: { name: string; slug: string }[]) => void;
  isGeneratingCategories: boolean;
  setIsGeneratingCategories: (val: boolean) => void;
  isConnecting: boolean;
  setIsConnecting: (val: boolean) => void;
  saving: boolean;
  setSaving: (val: boolean) => void;
  postingDays: string[];
  setPostingDays: (val: string[]) => void;
  isSocialMediaEnabled: boolean;
  setIsSocialMediaEnabled: (val: boolean) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (val: boolean) => void;
  lastSavedValues: any;
  setLastSavedValues: (val: any) => void;
  imageGenerationEnabled: boolean;
  setImageGenerationEnabled: (val: boolean) => void;

};

const SettingsStateContext = createContext<SettingsStateContextType | undefined>(
  undefined
);

export const SettingsStateProvider = ({ children }: { children: ReactNode }) => {
  const { writingStyle, subjectMatters, formattemplate } = useSettings();
  const { currentWebsite } = useWebsites();
  const [styleInput, setStyleInput] = useState(writingStyle);
  const [subjects, setSubjects] = useState<string[]>(subjectMatters);
  const [newSubject, setNewSubject] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [wpUrl, setWpUrl] = useState("");
  const [wpUsername, setWpUsername] = useState("");
  const [wpPassword, setWpPassword] = useState("");
  const [generatingSubject, setGeneratingSubject] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionDiagnostics, setConnectionDiagnostics] = useState(null);
  const [directWpSettings, setDirectWpSettings] = useState<WordPressSettings | null>(
    null
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [wpFormatOpen, setWpFormatOpen] = useState(false);
  const [htmlTemplate, setHtmlTemplate] = useState(formattemplate);
  const [sendingTestPost, setSendingTestPost] = useState(false);
  const [testPostId, setTestPostId] = useState<number | null>(null);
  const [testPostUrl, setTestPostUrl] = useState<string | null>(null);
  const [websiteLanguage, setWebsiteLanguage] = useState<string>(
    currentWebsite?.language || "en"
  );
  const [customLanguageModalOpen, setCustomLanguageModalOpen] = useState(false);
  const [customLanguageInput, setCustomLanguageInput] = useState("");
  const [wpPublishStatus, setWpPublishStatus] = useState<string>("draft");
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [generatedCategories, setGeneratedCategories] = useState<
    { name: string; slug: string }[]
  >([]);
  const [isGeneratingCategories, setIsGeneratingCategories] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postingDays, setPostingDays] = useState<string[]>([]);
  const [isSocialMediaEnabled, setIsSocialMediaEnabled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedValues, setLastSavedValues] = useState({
    posting_frequency: 3,
    writing_style: "Professional",
    subject_matters: ["guide", "tips", "best practices", "how to", "introduction"],
    format_template: defaultFormatTemplate,
    image_prompt: "",
    image_model: DEFAULT_IMAGE_MODEL,
    negative_prompt: "",
    weekly_planning_day: "friday",
    content_policies: "default",
  });
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);


  return (
    <SettingsStateContext.Provider
      value={{
        styleInput,
        setStyleInput,
        subjects,
        setSubjects,
        newSubject,
        setNewSubject,
        isSaving,
        setIsSaving,
        isAuthenticating,
        setIsAuthenticating,
        showAuthDialog,
        setShowAuthDialog,
        wpUrl,
        setWpUrl,
        wpUsername,
        setWpUsername,
        wpPassword,
        setWpPassword,
        generatingSubject,
        setGeneratingSubject,
        connectionError,
        setConnectionError,
        connectionDiagnostics,
        setConnectionDiagnostics,
        directWpSettings,
        setDirectWpSettings,
        categories,
        setCategories,
        wpFormatOpen,
        setWpFormatOpen,
        htmlTemplate,
        setHtmlTemplate,
        sendingTestPost,
        setSendingTestPost,
        testPostId,
        setTestPostId,
        testPostUrl,
        setTestPostUrl,
        websiteLanguage,
        setWebsiteLanguage,
        customLanguageModalOpen,
        setCustomLanguageModalOpen,
        customLanguageInput,
        setCustomLanguageInput,
        wpPublishStatus,
        setWpPublishStatus,
        showCategoryDialog,
        setShowCategoryDialog,
        generatedCategories,
        setGeneratedCategories,
        isGeneratingCategories,
        setIsGeneratingCategories,
        isConnecting,
        setIsConnecting,
        saving,
        setSaving,
        postingDays,
        setPostingDays,
        isSocialMediaEnabled,
        setIsSocialMediaEnabled,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        lastSavedValues,
        setLastSavedValues,
        imageGenerationEnabled,
        setImageGenerationEnabled,

      }}
    >
      {children}
    </SettingsStateContext.Provider>
  );
};

export const useSettingsState = () => {
  const context = useContext(SettingsStateContext);
  if (context === undefined) {
    throw new Error("useSettingsState must be used within a SettingsStateProvider");
  }
  return context;
};
